import { useState } from 'react';
import { use_geolocation } from '@/lib/use-geolocation';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, Clock, Loader2, MapPin, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ProviderAvatar, is_at_your_door } from '@/components/provider-avatar';
import { FavouriteVendorButton } from '@/components/favourite-vendor-button';
import { StatusPill } from '@/components/ui/status-pill';
import { ErrorState } from '@/components/ui/error-state';
import { AuthImage } from '@/components/auth-image';
import { ImageLightbox } from '@/components/image-lightbox';
import { use_vendor_detail } from '@/features/discovery/hooks';
import { MODULE_LABEL, type DiscoveryService, type VendorPhotoLite } from '@/features/discovery/types';
import { LANGUAGE_OPTIONS, WALKER_SERVICE_RADIUS_OPTIONS } from '@/features/walker/types';
import { VendorReviewsSection } from '@/components/vendor-reviews-section';
import { MenuImagesViewer } from '@/components/menu-images-viewer';
import { DiningChips, dining_kind_label } from '@/components/dining-capability-chips';
import { DiningBookingSection } from '@/components/dining-booking-section';
import { GroomingPackagesSection } from '@/components/grooming-packages-section';
import { use_public_grooming_packages } from '@/features/grooming-package/hooks';
import { ServicePackagesSection } from '@/components/service-packages-section';
import { ImageCarousel } from '@/components/image-carousel';

/** Hero + thumbnail rail; opens a fullscreen lightbox. */
function PhotoGallery({ photos }: { photos: VendorPhotoLite[] }) {
  const [open_at, set_open_at] = useState<number | null>(null);
  // The hero pages left/right with arrow buttons (client change, 9 Oct 2026).
  const [index, set_index] = useState(0);
  if (photos.length === 0) return null;
  const paths = photos.map((p) => p.file_path);
  const current = Math.min(index, paths.length - 1);
  const go = (by: -1 | 1) => set_index((i) => (i + by + paths.length) % paths.length);

  return (
    <div className="flex flex-col gap-2" data-testid="vendor-gallery">
      <div className="relative">
        <button
          type="button"
          onClick={() => set_open_at(current)}
          className="relative aspect-[16/9] w-full overflow-hidden rounded-card border-2 border-brand-outline-variant/20 bg-muted shadow-clay"
          aria-label="View photos"
          data-testid="gallery-hero"
        >
          <AuthImage
            path={`${paths[current]}?variant=medium`}
            alt="Vendor"
            className="h-full w-full object-cover"
          />
        </button>
        {paths.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Previous photo"
              className="absolute left-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border-2 border-input bg-card shadow-clay-sm"
              data-testid="gallery-prev"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Next photo"
              className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border-2 border-input bg-card shadow-clay-sm"
              data-testid="gallery-next"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}
      </div>
      {photos.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {photos.map((p, i) => (
            <button
              key={p.id}
              type="button"
              onClick={() => set_index(i)}
              data-testid={`gallery-thumb-${i}`}
              className={
                'h-16 w-16 shrink-0 overflow-hidden rounded-button border-2 transition-opacity hover:opacity-80 ' +
                (i === current ? 'border-primary' : 'border-brand-outline-variant/20')
              }
            >
              <AuthImage path={`${p.file_path}?variant=thumb`} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
      {open_at !== null && (
        <ImageLightbox
          paths={paths}
          index={open_at}
          on_close={() => set_open_at(null)}
          on_navigate={set_open_at}
        />
      )}
    </div>
  );
}

function humanize(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\bcodes?\b/i, '')
    .replace(/\binr\b/i, '(₹)')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

const LANGUAGE_BY_CODE: Record<string, string> = Object.fromEntries(
  LANGUAGE_OPTIONS.map((l) => [l.code, l.display_name]),
);
const RADIUS_BY_VALUE: Record<string, string> = Object.fromEntries(
  WALKER_SERVICE_RADIUS_OPTIONS.map((r) => [r.value, r.label]),
);

/**
 * Module details arrive as raw stored values — enum members, radius keys and
 * ISO language codes. This is the page a customer uses to choose a vendor, so
 * render them the way the vendor saw them while onboarding rather than leaking
 * `KM_5` / `DOG_WALKING` / `en`.
 */
function format_scalar(value: string): string {
  if (RADIUS_BY_VALUE[value]) return RADIUS_BY_VALUE[value];
  if (LANGUAGE_BY_CODE[value]) return LANGUAGE_BY_CODE[value];
  // SCREAMING_SNAKE enum → sentence case: DOG_WALKING → Dog walking.
  if (/^[A-Z0-9]+(_[A-Z0-9]+)*$/.test(value) && /[A-Z]/.test(value)) {
    const words = value.replace(/_/g, ' ').toLowerCase();
    return words.charAt(0).toUpperCase() + words.slice(1);
  }
  return value;
}

function format_value(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (Array.isArray(value)) {
    return value.length ? value.map((v) => format_scalar(String(v))).join(', ') : null;
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  return format_scalar(String(value));
}

function ModuleCard({ type, details }: { type: string; details: Record<string, unknown> }) {
  const rows = Object.entries(details)
    .map(([k, v]) => [k, format_value(v)] as const)
    .filter(([, v]) => v !== null);
  if (rows.length === 0) return null;
  return (
    <Card data-testid={`module-card-${type}`}>
      <CardHeader>
        <CardTitle>{MODULE_LABEL[type] ?? type}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col">
        {rows.map(([k, v]) => (
          <div key={k} className="grid grid-cols-3 gap-3 border-b-2 border-brand-outline-variant/10 py-2 text-sm last:border-b-0">
            <span className="font-semibold text-muted-foreground">{humanize(k)}</span>
            <span className="col-span-2">{v}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function BookableServices({
  services,
  vendor_name,
  vendor_id,
}: {
  services: DiscoveryService[];
  vendor_name: string | null;
  vendor_id: string;
}) {
  const navigate = useNavigate();
  if (services.length === 0) return null;
  return (
    <Card data-testid="bookable-services">
      <CardHeader>
        <CardTitle>Book a service</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col p-0">
        <ul className="divide-y-2 divide-brand-outline-variant/10">
          {services.map((s) => (
            <li key={s.id} className="flex items-center gap-3 p-4" data-testid={`service-${s.id}`}>
              {s.images.length > 0 && (
                <ImageCarousel images={s.images} size="h-16 w-16" className="shrink-0 self-start" />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">{s.name}</span>
                  <StatusPill tone="neutral">{MODULE_LABEL[s.module_type] ?? s.module_type}</StatusPill>
                  {s.duration_minutes != null && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" /> {s.duration_minutes} min
                    </span>
                  )}
                  {/* This service's own rating (client change, 9 Oct 2026). */}
                  {s.avg_rating != null && (
                    <span className="flex items-center gap-1 text-xs font-semibold" data-testid={`service-rating-${s.id}`}>
                      <Star className="h-3 w-3 fill-warning text-warning" /> {s.avg_rating.toFixed(1)}
                      <span className="font-normal text-muted-foreground">({s.review_count ?? 0})</span>
                    </span>
                  )}
                </div>
                {s.description && <p className="mt-0.5 truncate text-xs text-muted-foreground">{s.description}</p>}
              </div>
              <div className="shrink-0 font-heading text-base font-bold tabular-nums">₹{s.price_inr}</div>
              <div className="flex shrink-0 flex-col gap-1.5">
                <Button
                  size="sm"
                  variant="gradient"
                  onClick={() => navigate(`/app/book/${s.id}`, { state: { service: s, vendor_name, vendor_id } })}
                  data-testid={`book-${s.id}`}
                >
                  Book
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

export function VendorDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  // Reuse the location the shopper already granted on Discover, if any — the
  // server needs it to fill in distance_km on the vendor and its services.
  const geo = use_geolocation();
  const detail_q = use_vendor_detail(id, { lat: geo.lat, lng: geo.lng });
  const v = detail_q.data;
  const is_groomer = !!v?.module_types.includes('GROOMER');
  // Grooming packages (tiers) render in their own section; hide their backing
  // VendorService rows from the plain à-la-carte "Book a service" list.
  const groomer_packages_q = use_public_grooming_packages(id ?? '', { enabled: is_groomer });
  const package_service_ids = new Set((groomer_packages_q.data ?? []).map((p) => p.service_id));
  const bookable_services = v ? v.services.filter((s) => !package_service_ids.has(s.id)) : [];

  return (
    <div className="flex flex-col gap-5">
      <Button variant="ghost" size="sm" className="self-start" onClick={() => navigate('/app/discover')}>
        <ArrowLeft className="h-4 w-4" /> Back to results
      </Button>

      {detail_q.isLoading ? (
        <div className="flex items-center justify-center p-12 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : detail_q.isError || !v ? (
        <ErrorState
          data-testid="vendor-detail-error"
          title="Vendor unavailable"
          description="This vendor isn't available right now. Try browsing other providers."
        />
      ) : (
        <>
          <PhotoGallery photos={v.photos} />
          <Card data-testid="vendor-detail">
            <CardHeader className="flex flex-row items-start justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                {/*
                  For a walker or trainer the vendor IS a person, so lead with their face —
                  this is who turns up at the door. Premises modules keep the storefront
                  gallery above as their visual identity and get no avatar here.
                */}
                {is_at_your_door(v.module_types) && (
                  <ProviderAvatar
                    path={v.profile_photo_path}
                    name={v.business_name}
                    size="md"
                    data-testid="vendor-profile-avatar"
                  />
                )}
                <div className="min-w-0">
                  <CardTitle className="truncate">{v.business_name ?? 'Unnamed vendor'}</CardTitle>
                  {/* Restaurants & cafés: rating and distance in the title (client change, 9 Oct 2026). */}
                  {v.dining && (
                    <p className="mt-1 flex flex-wrap items-center gap-x-2 text-sm" data-testid="dining-title-meta">
                      {v.review_count > 0 && (
                        <span className="flex items-center gap-1 font-semibold">
                          <Star className="h-4 w-4 fill-warning text-warning" /> {v.avg_rating.toFixed(1)}
                        </span>
                      )}
                      {v.distance_km != null && (
                        <span className="text-muted-foreground">
                          {v.distance_km <= 5 ? 'Within 5 km' : `${v.distance_km.toFixed(1)} km away`}
                        </span>
                      )}
                    </p>
                  )}
                </div>
              </div>
              <FavouriteVendorButton vendor_id={v.id} vendor_name={v.business_name} />
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-3">
                {v.review_count > 0 ? (
                  <span className="flex items-center gap-1 text-sm">
                    <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                    {v.avg_rating.toFixed(1)} ({v.review_count} reviews)
                  </span>
                ) : (
                  <span className="text-sm text-muted-foreground">No reviews yet</span>
                )}
                {v.is_24x7 && <StatusPill tone="info">Open 24×7</StatusPill>}
              </div>

              {v.dining && (
                <div className="flex flex-wrap items-center gap-2">
                  <StatusPill tone="neutral">{dining_kind_label(v.dining.kind)}</StatusPill>
                  <DiningChips dining={v.dining} />
                </div>
              )}

              {v.description && <p className="text-sm">{v.description}</p>}

              <div className="flex flex-wrap gap-1.5">
                {v.module_types.map((m) => (
                  <StatusPill key={m} tone="neutral">
                    {MODULE_LABEL[m] ?? m}
                  </StatusPill>
                ))}
              </div>

              {(v.address || v.city) && (
                <p className="flex items-start gap-1.5 text-sm text-muted-foreground">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    {[v.address, v.landmark, v.city, v.state, v.pincode].filter(Boolean).join(', ')}
                    {v.distance_km != null && (
                      <span className="ml-1.5 font-semibold text-primary" data-testid="vendor-detail-distance">
                        · {v.distance_km.toFixed(1)} km away
                      </span>
                    )}
                  </span>
                </p>
              )}
              {/* No website link on the detail screen (client change, 9 Oct 2026). */}
            </CardContent>
          </Card>

          {v.dining && (
            <DiningBookingSection
              offerings={v.offerings}
              walk_in={v.dining.walk_in}
              vendor_name={v.business_name}
              vendor_id={v.id}
            />
          )}

          <BookableServices services={bookable_services} vendor_name={v.business_name} vendor_id={v.id} />

          {is_groomer && <GroomingPackagesSection vendor_id={v.id} vendor_name={v.business_name} />}
          {/* Walker/trainer packages replace the customer-built plan/course builder (client change, 9 Oct 2026). */}
          {(v.module_types.includes('WALKER') || v.module_types.includes('TRAINER')) && (
            <ServicePackagesSection vendor_id={v.id} vendor_name={v.business_name} />
          )}

          {Object.entries(v.module_details).map(([type, details]) => (
            <ModuleCard key={type} type={type} details={details} />
          ))}

          {/* Menu as photos; ordering happens at the venue (client change, 9 Oct 2026). */}
          <MenuImagesViewer images={v.menu_images ?? []} />

          <VendorReviewsSection vendor_id={v.id} review_count={v.review_count} />

        </>
      )}
    </div>
  );
}
